import React, { useState, useRef } from 'react'
import { motion, useScroll, useTransform, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'
import { marketingColors as colors, marketingBrand, DARK, PAPER, BOOKING_CALENDAR_URL, marketingEase as ease } from '../lib/marketingBrand'
import { ACCENT, PaperTextureOverlay } from '../components/marketing/MarketingChrome'
import { ArrowRight, Clock, ChevronDown, ChevronUp, Check } from 'lucide-react'

const images = {
  stock12: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock12.png',
}

// ============================================
// HERO SECTION
// ============================================
const Hero: React.FC = () => {
  const containerRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end start"] })
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

  return (
    <section ref={containerRef} className="relative min-h-[90vh] overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src={images.stock12}
          alt="New home"
          className="w-full h-full object-cover object-[center_30%]"
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(105deg, ${DARK}f2 0%, ${colors.tealDark}cc 50%, transparent 100%)` }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${DARK}e8, transparent 45%, ${colors.tealDark}99)` }} />
      </div>
      
      <motion.div style={{ opacity }} className="relative w-full min-h-[90vh] flex items-center">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 w-full py-32">
          <div className="max-w-2xl">
            {/* Label */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex items-center gap-4 mb-8"
            >
              <div className="w-8 h-[1px]" style={{ backgroundColor: colors.copper }} />
              <span className="text-sm tracking-[0.15em] uppercase" style={{ color: colors.copper }}>
                One-time move-in service
              </span>
            </motion.div>
            
            {/* Main Headline */}
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.28 }}
              className="text-white/85 font-sans text-sm sm:text-base max-w-xl leading-relaxed mb-4"
            >
              <strong className="font-semibold text-white">MyHome SmartStart™</strong> is a single visit after closing: we reset
              locks, detectors, filters, and key systems so buyers start safe—not guessing what the last owner skipped.
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="font-serif text-white"
              style={{ fontSize: 'clamp(2.5rem, 7vw, 4.25rem)', lineHeight: 1.05, letterSpacing: '-0.02em' }}
            >
              Move-In Ready,
            </motion.h1>
            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="font-serif italic"
              style={{ fontSize: 'clamp(2.5rem, 7vw, 4.25rem)', lineHeight: 1.05, letterSpacing: '-0.02em', color: colors.copper }}
            >
              Not a Question Mark
            </motion.h1>
            
            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-white/70 mt-6 text-base sm:text-lg leading-relaxed max-w-xl"
            >
              Built for new homeowners and for agents who want a closing gift that protects the relationship—not just a basket.
            </motion.p>
            
            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 mt-10"
            >
              <Link 
                to="/pricing"
                className="group inline-flex items-center justify-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase text-white transition-all duration-500 hover:brightness-110"
                style={{ backgroundColor: colors.copper }}
              >
                See pricing
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a 
                href="/partners"
                className="inline-flex items-center justify-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase border border-white/30 text-white transition-all duration-300 hover:bg-white/10"
              >
                Partner With Us
              </a>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  )
}

// ============================================
// WHAT IT IS (scannable)
// ============================================
const WhatIsSmartStartStrip: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-60px' })
  const blocks = [
    {
      src: ACCENT.iconKey,
      title: 'Security first',
      body: 'Locks rekeyed, detectors addressed, emergency shutoffs located and breakers labeled.',
    },
    {
      src: ACCENT.clipboardChecklist,
      title: 'Core systems checked',
      body: 'Filters, vents, and essentials verified so obvious gaps are closed before you settle in.',
    },
    {
      src: ACCENT.hudsonAtDoor,
      title: 'One visit, documented',
      body: 'Essential (~2 hrs) or Complete (~3 hrs) with optional health snapshot on Complete—see packages below for every line item.',
    },
  ]
  return (
    <section ref={ref} className="relative py-20 md:py-28 overflow-hidden" style={{ backgroundColor: colors.tealDark }}>
      <PaperTextureOverlay opacity={0.025} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease }}
          className="text-center font-serif text-xl sm:text-2xl md:text-3xl mb-10 md:mb-14 text-white"
        >
          What MyHome SmartStart™ is (in plain terms)
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-10 md:gap-8">
          {blocks.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 28 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.08 + i * 0.1, ease }}
              className="relative overflow-hidden rounded-sm p-8 md:p-10"
              style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
            >
              <img
                src={b.src}
                alt=""
                className="absolute inset-0 w-full h-full object-contain object-center pointer-events-none select-none"
                style={{ mixBlendMode: 'soft-light', opacity: 0.08 }}
                loading="lazy"
              />
              <div className="relative z-10">
                <h3 className="font-serif text-lg mb-2 text-white">
                  {b.title}
                </h3>
                <p className="font-sans text-sm sm:text-base leading-relaxed text-white/70">
                  {b.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ============================================
// AGENT BENEFITS
// ============================================
const AgentBenefits: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const benefits = [
    { title: 'Thoughtful & Practical', description: 'A closing gift that actually protects their investment' },
    { title: 'Protects After Closing', description: 'Shows you care beyond the transaction' },
    { title: 'Builds Trust', description: 'Reinforces long-term value and relationship' },
    { title: 'Positions You as Advocate', description: 'Not just a salesperson—a true partner' },
  ]

  return (
    <section ref={ref} className="relative py-28 md:py-36 lg:py-48 overflow-hidden" style={{ backgroundColor: PAPER }}>
      <PaperTextureOverlay opacity={0.035} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease }}
          className="text-center mb-16"
        >
          <span className="font-sans text-sm font-semibold uppercase tracking-[0.15em]" style={{ color: colors.copper }}>
            For Real Estate Agents
          </span>
          <h2 className="font-serif mt-4" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, color: colors.tealDark }}>
            Why agents bundle SmartStart at closing
          </h2>
          <p className="font-sans mt-4 max-w-xl mx-auto leading-relaxed" style={{ color: colors.drillbitSkies }}>
            SmartStart is the one-time move-in reset (locks, detectors, filters, systems orientation). Give buyers that—not only a
            basket—and you stay the agent who thought past the transaction.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease }}
              className="p-8"
              style={{ borderBottom: `3px solid ${colors.copper}` }}
            >
              <h3 className="font-serif text-lg mb-2" style={{ color: colors.tealDark }}>{benefit.title}</h3>
              <p className="font-sans text-sm leading-relaxed" style={{ color: colors.drillbitSkies }}>{benefit.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4, ease }}
          className="mt-16 p-8 lg:p-12"
          style={{ backgroundColor: colors.cream }}
        >
          <p className="font-serif text-xl lg:text-2xl italic leading-relaxed" style={{ color: colors.tealDark }}>
            &ldquo;This takes care of the things every homeowner <em>hopes</em> were done—but never really knows.&rdquo;
          </p>
          <p className="font-sans text-sm font-medium mt-4" style={{ color: colors.copper }}>
            — What to tell your client at closing
          </p>
        </motion.div>
      </div>
    </section>
  )
}

// ============================================
// TARGET MARKET
// ============================================
const TargetMarket: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  return (
    <section ref={ref} className="relative py-24 md:py-32 overflow-hidden" style={{ backgroundColor: colors.cream }}>
      <PaperTextureOverlay opacity={0.02} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease }}
          >
            <h2 className="font-serif mb-8" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', lineHeight: 1.1, color: colors.tealDark }}>
              Perfect For
            </h2>
            <div className="space-y-4">
              {[
                'Real Estate Agents as closing gifts',
                'New Homeowners',
                'First-Time Homebuyers',
                'Pending closing transactions',
              ].map((text, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.7, delay: index * 0.1, ease }}
                  className="flex items-center gap-4 p-4"
                  style={{ borderLeft: `3px solid ${colors.copper}` }}
                >
                  <span className="font-sans font-medium" style={{ color: colors.tealDark }}>{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease }}
          >
            <h2 className="font-serif mb-8" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', lineHeight: 1.1, color: colors.tealDark }}>
              Not Designed For
            </h2>
            <div className="p-8" style={{ borderLeft: `3px solid ${colors.copper}` }}>
              <p className="font-serif font-medium mb-2" style={{ color: colors.tealDark }}>New Construction Builds</p>
              <p className="font-sans text-sm leading-relaxed" style={{ color: colors.drillbitSkies }}>
                New builds come with builder warranties and fresh installations. SmartStart is designed specifically for homes that have changed hands and need a verified reset.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ============================================
// WHAT'S INCLUDED — product value (no pricing; see Pricing page)
// ============================================
const SmartStartValue: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const [expandedTier, setExpandedTier] = useState<'essential' | 'complete' | null>(null)

  const essentialServices = [
    'Replace all exterior door locks (client provided)',
    'Rekey garage keypad',
    'Replace smoke & CO detectors or batteries',
    'Fire extinguisher in kitchen',
    'Locate emergency shutoffs (water, gas)',
    'Label all breakers on electrical panel',
    'Clean dryer vent & lint trap housing',
    'Replace HVAC filters',
    'Replace fridge water filter',
  ]

  const completeOnlyServices = [
    'Clean & inspect all exhaust fans',
    'Inspect caulking around tubs, sinks, toilets',
    'Inspect & clean all under-sink plumbing',
    'Swap toilet seats',
    'Flush water heater & check temperature',
    'Check windows & door seals',
    'Deep clean appliances (fridge coils, dishwasher filter, etc.)',
    'MyHome Health Snapshot™ report — documented condition of key systems & maintenance priorities',
  ]

  return (
    <section ref={ref} className="relative py-28 md:py-36 lg:py-48 overflow-hidden" style={{ backgroundColor: colors.tealDark }}>
      <img
        src={ACCENT.houseCrosssection}
        alt=""
        className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        style={{ mixBlendMode: 'soft-light', opacity: 0.06 }}
      />
      <PaperTextureOverlay opacity={0.02} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-14"
        >
          <span className="font-sans text-sm font-semibold uppercase tracking-[0.15em]" style={{ color: colors.copper }}>
            Packages
          </span>
          <h2 className="font-serif text-white mt-4" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1 }}>
            Two packages. One goal: your home safe, secure, and ready.
          </h2>
          <p className="text-white/70 font-sans mt-4 max-w-2xl mx-auto">
            We don&apos;t wait for things to break—we ensure your most valuable asset is protected from day one.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease }}
            className="rounded-sm overflow-hidden"
            style={{ backgroundColor: PAPER }}
          >
            <div className="p-8 lg:p-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-xl" style={{ color: colors.tealDark }}>Essential</h3>
                <span className="text-sm flex items-center gap-2" style={{ color: colors.drillbitSkies }}><Clock className="w-4 h-4" /> ~2 hours</span>
              </div>
              <p className="font-sans text-base mb-6" style={{ color: colors.drillbitSkies }}>
                Everything you need to start fresh with confidence: safety, security, and core systems verified by a trained specialist.
              </p>
              <button
                onClick={() => setExpandedTier(expandedTier === 'essential' ? null : 'essential')}
                className="w-full flex items-center justify-between py-3 px-4 rounded-sm font-sans font-medium transition-colors mb-6"
                style={{ backgroundColor: `${colors.teal}15`, color: colors.teal }}
              >
                <span>View all services</span>
                {expandedTier === 'essential' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedTier === 'essential' && (
                <ul className="space-y-3 mb-6">
                  {essentialServices.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-sans" style={{ color: colors.drillbitSkies }}>
                      <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: colors.teal }} />
                      {s}
                    </li>
                  ))}
                </ul>
              )}
              <div className="space-y-3">
                <Link to="/pricing" className="block w-full text-center py-3.5 rounded-sm font-sans font-medium border-2" style={{ borderColor: colors.teal, color: colors.teal }}>See pricing</Link>
                <a
                  href="https://api.leadconnectorhq.com/widget/booking/vGBU75grNpdx4TI2SvX2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center py-3.5 rounded-sm font-sans font-medium text-white"
                  style={{ backgroundColor: colors.teal }}
                >
                  Book Your Consultation
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.12, ease }}
            className="rounded-sm overflow-hidden relative"
            style={{ backgroundColor: PAPER, border: `2px solid ${colors.copper}` }}
          >
            <div className="absolute top-0 left-0 right-0 py-2 text-center text-sm font-sans font-semibold uppercase tracking-widest text-white" style={{ backgroundColor: colors.copper }}>Recommended</div>
            <div className="p-8 lg:p-10 pt-12">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-xl" style={{ color: colors.tealDark }}>Complete</h3>
                <span className="text-sm flex items-center gap-2" style={{ color: colors.drillbitSkies }}><Clock className="w-4 h-4" /> ~3 hours</span>
              </div>
              <p className="font-sans text-base mb-6" style={{ color: colors.drillbitSkies }}>
                A full home reset—nothing left to question. Everything in Essential, plus deeper systems care and a documented MyHome Health Snapshot™.
              </p>
              <button
                onClick={() => setExpandedTier(expandedTier === 'complete' ? null : 'complete')}
                className="w-full flex items-center justify-between py-3 px-4 rounded-sm font-sans font-medium transition-colors mb-6"
                style={{ backgroundColor: `${colors.teal}15`, color: colors.teal }}
              >
                <span>View all services</span>
                {expandedTier === 'complete' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedTier === 'complete' && (
                <ul className="space-y-3 mb-6">
                  <p className="text-sm font-sans font-medium" style={{ color: colors.teal }}>Everything in Essential, plus:</p>
                  {completeOnlyServices.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-sans" style={{ color: colors.drillbitSkies }}>
                      <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: colors.teal }} />
                      {s}
                    </li>
                  ))}
                </ul>
              )}
              <div className="space-y-3">
                <Link to="/pricing" className="block w-full text-center py-3.5 rounded-sm font-sans font-medium text-white" style={{ backgroundColor: colors.copper }}>See pricing</Link>
                <a
                  href="https://api.leadconnectorhq.com/widget/booking/vGBU75grNpdx4TI2SvX2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center py-3.5 rounded-sm font-sans font-medium text-white"
                  style={{ backgroundColor: colors.teal }}
                >
                  Book Your Consultation
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-center mt-12"
        >
          <p className="font-sans text-white/60">Agents: offer SmartStart as a closing gift. Custom options available.</p>
        </motion.div>
      </div>
    </section>
  )
}

// ============================================
// CTA SECTION
// ============================================
const CTASection: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  return (
    <section ref={ref} className="py-28 md:py-36 lg:py-48" style={{ backgroundColor: colors.tealDark }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease }}
        >
          <h2 className="font-serif text-white" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1 }}>
            Ready to Elevate Your Closing Gifts?
          </h2>
          <p className="text-white/70 mt-4 max-w-xl mx-auto text-lg">
            Partner with Hudson to offer your clients something truly memorable and valuable.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center mt-10">
            <a
              href={BOOKING_CALENDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase transition-all duration-500 hover:brightness-110 text-white"
              style={{ backgroundColor: colors.copper }}
            >
              Book Your Consultation
            </a>
            <a 
              href="/partners"
              className="group inline-flex items-center justify-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase border border-white/30 text-white transition-all duration-300 hover:bg-white/10"
            >
              Become a Partner Agent
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a 
              href="mailto:info@hudsonmyhome.com"
              className="inline-flex items-center justify-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase border border-white/30 text-white transition-all duration-300 hover:bg-white/10"
            >
              Contact Us
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

// ============================================
// MAIN PAGE
// ============================================
const SmartStartPage: React.FC = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: marketingBrand.tapeTurquoise }}>
      <MetaTags
        title="MyHome SmartStart | New Home Setup | Hudson My Home"
        description="MyHome SmartStart is a one-time move-in visit: locks, detectors, filters, and systems orientation so new buyers start safe. From $299."
      />
      <Navbar />
      <main>
        <Hero />
        <WhatIsSmartStartStrip />
        <AgentBenefits />
        <TargetMarket />
        <SmartStartValue />
        <CTASection />
      </main>
      <Footer />
    </div>
  )
}

export default SmartStartPage
